import { Pipe, PipeTransform } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'

import { RecipientType } from '@shared/modules/notifications/shared/notification.model'
import { RecipientLabels } from '@core/constants/notifications'
import { Role } from '@shared/models/role.model'
import { User } from '@shared/models/user.model'
import { UserGroup } from '@shared/models/user-group.model'
import { Team } from '@shared/models/team.model'

export interface RecipientScopeItem {
  id: number | string
  label: string
  team: Team
}

export type RoleItem = Pick<Role, 'displayName'>
export type UserItem = Pick<User, 'name' | 'surname'>
export type UserGroupItem = Pick<UserGroup, 'name'>

export type RecipientValue = (RoleItem | UserItem | UserGroupItem | string)[]

export interface Recipient {
  type: RecipientType
  value: RecipientValue
}

@Pipe({
  name: 'recipients',
  pure: false,
  standalone: false,
})
export class RecipientsPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  private formatLabel(str: string[]): string {
    const valueToString = (str: string[]): string => str.join(', ')

    if (str.length > 4) {
      const truncatedValue = str.slice(0, 5)
      return `${valueToString(truncatedValue)}, ...`
    }
    return valueToString(str)
  }

  private recipientRoleValue(value: RecipientValue): string[] {
    const toRoleValue = (item: RoleItem): string => item.displayName
    return value.map(toRoleValue)
  }

  private recipientUserValue(value: RecipientValue): string[] {
    const toUserValue = (item: UserItem): string => `${item.name} ${item.surname}`
    return value.map(toUserValue)
  }

  private recipientUserGroupValue(value: RecipientValue): string[] {
    const toUserGroupValue = (item: UserGroupItem): string => item.name
    return value.map(toUserGroupValue)
  }

  private recipientValueLabel({ type, value }: Recipient): string[] {
    switch (type) {
      case RecipientType.Role:
        return this.recipientRoleValue(value)
      case RecipientType.UserList:
        return this.recipientUserValue(value)
      case RecipientType.UserGroupList:
        return this.recipientUserGroupValue(value)
      default:
        return ['']
    }
  }

  transform(recipients: Recipient[]): string {
    if (!recipients || !Array.isArray(recipients)) {
      return '-'
    }

    const isMoreType = (recipient: Recipient) =>
      [RecipientType.OtherEmail, RecipientType.OtherPhone].includes(recipient.type) && recipient.value?.length
    const recipientMore = recipients.some(isMoreType)

    const isDisplayType = (recipient: Recipient) =>
      [RecipientType.Role, RecipientType.UserList, RecipientType.UserGroupList].includes(recipient.type)
    const recipientToDisplay = recipients.find(isDisplayType)

    const typeLabel = <string>this.translateService.instant(RecipientLabels[recipientToDisplay.type] as string)
    const recipientValue = this.recipientValueLabel(recipientToDisplay)
    const valuesLabel = this.formatLabel(recipientValue)
    const defaultLabel = valuesLabel ? `${typeLabel}\n${valuesLabel}` : ''

    const moreLabel = '+other...'
    const recipientLabel = defaultLabel ? `${defaultLabel}\n${moreLabel}` : moreLabel

    return recipientMore ? recipientLabel : defaultLabel
  }
}
